
import { 
  UpgradableNumber,
  RollPlusUpgradable,
  WeaponLimitation,
  AbilityLimitation,
  SpellLimitation, 
} from "@dndModels";
import { FloatingFeature } from "@models";
import { ABILITY_SCORES_EXPANDED, ATTACK_TYPES, DAMAGE_TYPES, SCHOOLS } from "@dndModels/Constants";
import { AttackType, DamageType, ModifierTarget, ModType, SpellLevel } from "@dndModels/Types";

import CommonUtilities from "@utilities/common_utilities";

const common_util = CommonUtilities.getInstance();

/**
 * This is supposed to represent a modification to something as part
 * of a Feature.  It modifies the thing in the modifies property,
 * and uses a formula for determining how to modify it.  
 * This can modify all kinds of things, from AC, to Spell Attack, to ASI, to Racial ASI.
 * I need to rethink things that give choices.  
 * I'm thinking I'd prefer to make those work with something like Feature Choices, and then each choice can have its own Modifier.
 * 
 * Examples:
 * 
 * ASI(): 1, Modifies ChoiceAS
 * GnomeInt(): 2, Modifies IntAS
 * 
 * Monk:
 * UnarmoredDefense(): ({armor} ? (10 + {DEX}) : (10 + {DEX} + {WIS})), Modifies BaseAC 
 * MartialArtsAttack(): ({armor} || {shield} || {weapons.filter(w => w.keywords.notIncludes("Monk")).length} > 0 ? {d20} + {STR} : {d20} + MAX({STR},{DEX})), modifies Unarmed Strike Attack and Base Weapon Attack
 * MartialArtsUnarmedStrike(): ({armor} || {shield} || {weapons.filter(w => w.keywords.notIncludes("Monk")).length} > 0 ? {STR} : {d4} + MAX({STR},{DEX})), modifies Unarmed Strike Damage and Base Weapon Damage
 * 
 * Barbarian:
 * RageDamage(): ({rageActive} ? ({barbarianLevel} < 9 ? 2 : ({barbarianLevel} < 16 ? 3 : 4)))
 * UnarmoredDefence(): ({armor} ? (10 + {DEX}) : (10 + {DEX} + {CON})), Modifies BaseAC 
 * 
 */
export class Modifier extends FloatingFeature {
  // Removing
  modifies: ModifierTarget | "" = "";
  modifies_detail_2 = "";
  modifies_detail_3 = "";
  type = "Flat"; // flat (default), char level, class level
  multiply = false;
  replace_asm = false;
  old_amount = "0";
  upgradable_number_array: UpgradableNumber[] = [];

  // Keeping
  // If these rules are broken then ignore the modifier
  allowed_armor_types: string[] = ["Any"];
  required_armor_types: string[] = ["None"];
  excluded_weapon_keywords: string[] = [];
  // required_weapon_keywords: string[] = ["Any"];
  // Limits to which things of the given type are to be modified
  // For Skill Checks it's the ids of the skills.  For a bunch of things it's a list of ability score types.
  // For Attack and Damage it's Attack Types: ["Unarmed Strike","Weapon","Spell Attacks","Spell Saves"]
  modifies_details: string[] = [];
  selectable_key = "";
  rolls: RollPlusUpgradable = new RollPlusUpgradable(); // can only be attached to things that are already rolls: attacks, checks, saving throws.

  // New
  mod_type: ModType = "Add";
  amount_unb: UpgradableNumber = new UpgradableNumber();
  class_ids: string[] = []; // For mods to Spells, it's sometimes limited to spells from a class
  ability_scores: string[] = [];
  spell_list_ids: string[] = [];
  weapon_keywords: string[] = [];
  magic_schools: string[] = [];
  the_damage_types: string[] = [];
  damage_selectable_key = "";
  slot_levels: string[] = [];
  
  apply_to: ("Weapons" | "Abilities" | "Spells")[] = [];
  weapon_limit: WeaponLimitation = new WeaponLimitation();
  ability_limit: AbilityLimitation = new AbilityLimitation();
  spell_limit: SpellLimitation = new SpellLimitation();

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.modifies = obj.modifies ? obj.modifies : "";
      if (obj.modifies_details) {
        this.modifies_details = [...obj.modifies_details];
      } else if (obj.modifies_detail) {
        this.modifies_details.push(obj.modifies_detail);
      }
      this.modifies_detail_2 = obj.modifies_detail_2 ? obj.modifies_detail_2 : "";
      if (this.modifies_detail_2 === "undefined") {
        this.modifies_detail_2 = "";
      }
      this.modifies_detail_3 = obj.modifies_detail_3 ? obj.modifies_detail_3 : "";
      if (this.modifies_detail_3 === "undefined") {
        this.modifies_detail_3 = "";
      }
      this.multiply = obj.multiply ? obj.multiply : false;
      this.replace_asm = obj.replace_asm ? obj.replace_asm : false;
      this.type = obj.type ? obj.type : "Flat";
      this.old_amount = obj.amount ? obj.amount : "0";
      this.allowed_armor_types = obj.allowed_armor_types ? [...obj.allowed_armor_types] : ["ALL"];
      this.required_armor_types = obj.required_armor_types ? [...obj.required_armor_types] : ["None"];
      this.excluded_weapon_keywords = obj.excluded_weapon_keywords ? [...obj.excluded_weapon_keywords] : [];
      // this.required_weapon_keywords = obj.required_weapon_keywords ? [...obj.required_weapon_keywords] : ["Any"];
      this.upgradable_number_array = [];
      if (obj.upgradable_number_array && obj.upgradable_number_array.length > 0) {
        obj.upgradable_number_array.forEach((un: any) => {
          this.upgradable_number_array.push(new UpgradableNumber(un));
        });
      }
      this.rolls = obj.rolls ? new RollPlusUpgradable(obj.rolls) : new RollPlusUpgradable();
      
      if (obj.class_ids) {
        this.class_ids = [...obj.class_ids];
      } else {
        this.class_ids = [];
        if (this.modifies === "Table Value") {
          if (this.modifies_detail_2 !== "") {
            this.modifies_details.push(this.modifies_detail_2);
          }
          if (this.modifies_detail_3 === "") {
            this.modifies_details.push(this.modifies_detail_3);
          }
        } else {
          if (this.modifies_detail_2 !== "") {
            this.class_ids.push(this.modifies_detail_2);
          }
          if (this.modifies_detail_3 === "") {
            this.class_ids.push(this.modifies_detail_3);
          }
        }
      }
      // if (this._id === "671b0a014db403adc5e1bc84") {
      //   debugger;
      // }
      this.selectable_key = obj.selectable_key ? obj.selectable_key : "";
      this.spell_list_ids = obj.spell_list_ids ? obj.spell_list_ids : [];
      this.ability_scores = obj.ability_scores ? obj.ability_scores : [];
      this.weapon_keywords = obj.weapon_keywords ? obj.weapon_keywords : []; 
      this.magic_schools = obj.magic_schools ? obj.magic_schools : [];
      this.the_damage_types = obj.the_damage_types ? obj.the_damage_types : (obj.damage_types ? obj.damage_types : []);
      this.damage_selectable_key = obj.damage_selectable_key ? obj.damage_selectable_key : "";
      this.slot_levels = obj.slot_levels ? obj.slot_levels : [];
      this.excluded_weapon_keywords = this.excluded_weapon_keywords.filter(o => o !== "None" && o !== "Any");
      if (["Attack","Damage","Range"].includes(this.modifies)) {
        if (obj.apply_to) {
          this.apply_to = obj.apply_to;
          this.spell_limit = obj.spell_limit ? new SpellLimitation(obj.spell_limit) : new SpellLimitation();
          this.weapon_limit = obj.weapon_limit ? new WeaponLimitation(obj.weapon_limit) : new WeaponLimitation();
          this.ability_limit = obj.ability_limit ? new AbilityLimitation(obj.ability_limit) : new AbilityLimitation();
        } else {
          // if (this.name.includes("Potent Spellcasting")) {
          //   debugger;
          // }
          if (this.modifies_details.includes("Cantrip Attacks")) {
            this.modifies_details = this.modifies_details.filter(o => o !== "Cantrip Attacks");
          }
          if (this.modifies_details.includes("Cantrip Saves")) {
            this.modifies_details = this.modifies_details.filter(o => o !== "Cantrip Saves");
          }

          const attack_types = [...ATTACK_TYPES,...ABILITY_SCORES_EXPANDED,"Spell Attacks","Cantrip Attacks"]; 
          this.modifies_details = this.modifies_details.filter(o => attack_types.includes(o));

          // Convert to Limitation usage
          if (common_util.anyOverlaps(["Melee","Ranged","Bonus","Thrown","Light","Versatile"], this.modifies_details) || 
            this.weapon_keywords.length > 0 || this.excluded_weapon_keywords.length > 0) {
            this.apply_to.push("Weapons");
            this.weapon_limit.attack_types = this.modifies_details.filter(o => ["Melee","Ranged","Bonus","Thrown","Light","Versatile"].includes(o)) as AttackType[];
            this.weapon_limit.weapon_keyword_ids = this.weapon_keywords.filter(o => o !== "Any");
            this.weapon_limit.excluded_keyword_ids = this.excluded_weapon_keywords;
            this.weapon_limit.damage_types = this.the_damage_types.filter(o => DAMAGE_TYPES.includes(o)) as DamageType[];
          }
          if (common_util.anyOverlaps(["Melee Spell","Ranged Spell","Save","Spell Attacks","Cantrip Attacks"], this.modifies_details) ||
            this.class_ids.length > 0 || this.slot_levels.length > 0 || 
            this.magic_schools.length > 0 || this.spell_list_ids.length > 0 ||
            this.damage_selectable_key.length > 0) {
            this.apply_to.push("Spells");
            this.spell_limit.attack_types = this.modifies_details.filter(o => ["Melee Spell","Ranged Spell","Save"].includes(o)) as AttackType[];
            this.spell_limit.damage_types = this.the_damage_types.filter(o => DAMAGE_TYPES.includes(o)) as DamageType[];
            if (this.damage_selectable_key.length > 0) {
              this.spell_limit.damage_types.push("Selectable");
              this.spell_limit.damage_selectable_key = this.damage_selectable_key;
            }
            this.spell_limit.levels = this.slot_levels.includes("Any") ? [] : this.slot_levels.map(o => {
              if (o === "Cantrip") {
                return 0;
              } else {
                return +o as SpellLevel;
              }
            });
            this.spell_limit.schools = this.magic_schools.filter(o => SCHOOLS.includes(o)); 
            this.spell_limit.list_ids = this.spell_list_ids;
            if (this.class_ids.length > 0) {
              this.spell_limit.source_type = "game_class";
              this.spell_limit.source_ids = this.class_ids;
            }
          }

          // this.modifies_details = [];
          // this.weapon_keywords = [];
          // this.excluded_weapon_keywords = [];
          // this.the_damage_types = [];
          // this.damage_selectable_key = "";
          // this.slot_levels = [];
          // this.magic_schools = [];
          // this.spell_list_ids = [];
          // this.class_ids = [];
        }
      } 
      if (this.allowed_armor_types.includes("ALL")) {
        this.allowed_armor_types = ["Any"];
      }
      if (obj.amount_unb) {
        this.amount_unb = new UpgradableNumber(obj.amount_unb);
      } else {
        try {
          this.amount_unb = new UpgradableNumber(+this.old_amount);
        } catch (ex) {}
      }
      this.mod_type = obj.mod_type ? obj.mod_type : "Add";
      // if (this.modifies === "Unarmed Strikes") {
      //   console.log(obj);
      //   console.log(this);
      // }
      for (let i = 0; i < this.modifies_details.length; ++i) {
        switch(this.modifies_details[i]) {
          case "Strength": this.modifies_details[i] = "STR"; break;
          case "Dexterity": this.modifies_details[i] = "DEX"; break;
          case "Constitution": this.modifies_details[i] = "CON"; break;
          case "Intelligence": this.modifies_details[i] = "INT"; break;
          case "Wisdom": this.modifies_details[i] = "WIS"; break;
          case "Charisma": this.modifies_details[i] = "CHA"; break;
        }
      }
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.modifies = obj.modifies ? obj.modifies : "";
  //     if (obj.modifies_details) {
  //       this.modifies_details = [...obj.modifies_details];
  //     } else if (obj.modifies_detail) {
  //       this.modifies_details.push(obj.modifies_detail);
  //     }
  //     this.modifies_detail_2 = obj.modifies_detail_2 ? obj.modifies_detail_2 : "";
  //     if (this.modifies_detail_2 === "undefined") {
  //       this.modifies_detail_2 = "";
  //     }
  //     this.modifies_detail_3 = obj.modifies_detail_3 ? obj.modifies_detail_3 : "";
  //     if (this.modifies_detail_3 === "undefined") {
  //       this.modifies_detail_3 = "";
  //     }
  //     this.multiply = obj.multiply ? obj.multiply : false;
  //     this.replace_asm = obj.replace_asm ? obj.replace_asm : false;
  //     this.type = obj.type ? obj.type : "Flat";
  //     this.old_amount = obj.amount ? obj.amount : "0";
  //     this.allowed_armor_types = obj.allowed_armor_types ? [...obj.allowed_armor_types] : ["ALL"];
  //     this.required_armor_types = obj.required_armor_types ? [...obj.required_armor_types] : ["None"];
  //     this.excluded_weapon_keywords = obj.excluded_weapon_keywords ? [...obj.excluded_weapon_keywords] : [];
  //     // this.required_weapon_keywords = obj.required_weapon_keywords ? [...obj.required_weapon_keywords] : ["Any"];
  //     this.upgradable_number_array = [];
  //     if (obj.upgradable_number_array && obj.upgradable_number_array.length > 0) {
  //       obj.upgradable_number_array.forEach((un: any) => {
  //         this.upgradable_number_array.push(new UpgradableNumber(un));
  //       });
  //     }
  //     this.rolls = obj.rolls ? new RollPlusUpgradable(obj.rolls) : new RollPlusUpgradable();
      
  //     if (obj.class_ids) {
  //       this.class_ids = [...obj.class_ids];
  //     } else {
  //       this.class_ids = [];
  //       if (this.modifies === "Table Value") {
  //         if (this.modifies_detail_2 !== "") {
  //           this.modifies_details.push(this.modifies_detail_2);
  //         }
  //         if (this.modifies_detail_3 === "") {
  //           this.modifies_details.push(this.modifies_detail_3);
  //         }
  //       } else {
  //         if (this.modifies_detail_2 !== "") {
  //           this.class_ids.push(this.modifies_detail_2);
  //         }
  //         if (this.modifies_detail_3 === "") {
  //           this.class_ids.push(this.modifies_detail_3);
  //         }
  //       }
  //     }
  //     // if (this._id === "671b0a014db403adc5e1bc84") {
  //     //   debugger;
  //     // }
  //     this.selectable_key = obj.selectable_key ? obj.selectable_key : "";
  //     this.spell_list_ids = obj.spell_list_ids ? obj.spell_list_ids : [];
  //     this.ability_scores = obj.ability_scores ? obj.ability_scores : [];
  //     this.weapon_keywords = obj.weapon_keywords ? obj.weapon_keywords : []; 
  //     this.magic_schools = obj.magic_schools ? obj.magic_schools : [];
  //     this.the_damage_types = obj.the_damage_types ? obj.the_damage_types : (obj.damage_types ? obj.damage_types : []);
  //     this.damage_selectable_key = obj.damage_selectable_key ? obj.damage_selectable_key : "";
  //     this.slot_levels = obj.slot_levels ? obj.slot_levels : [];
  //     this.excluded_weapon_keywords = this.excluded_weapon_keywords.filter(o => o !== "None" && o !== "Any");
  //     if (["Attack","Damage","Range"].includes(this.modifies)) {
  //       if (obj.apply_to) {
  //         this.apply_to = obj.apply_to;
  //         this.spell_limit = obj.spell_limit ? new SpellLimitation(obj.spell_limit) : new SpellLimitation();
  //         this.weapon_limit = obj.weapon_limit ? new WeaponLimitation(obj.weapon_limit) : new WeaponLimitation();
  //         this.ability_limit = obj.ability_limit ? new AbilityLimitation(obj.ability_limit) : new AbilityLimitation();
  //       } else {
  //         // if (this.name.includes("Potent Spellcasting")) {
  //         //   debugger;
  //         // }
  //         if (this.modifies_details.includes("Cantrip Attacks")) {
  //           this.modifies_details = this.modifies_details.filter(o => o !== "Cantrip Attacks");
  //         }
  //         if (this.modifies_details.includes("Cantrip Saves")) {
  //           this.modifies_details = this.modifies_details.filter(o => o !== "Cantrip Saves");
  //         }

  //         const attack_types = [...ATTACK_TYPES,...ABILITY_SCORES_EXPANDED,"Spell Attacks","Cantrip Attacks"]; 
  //         this.modifies_details = this.modifies_details.filter(o => attack_types.includes(o));

  //         // Convert to Limitation usage
  //         if (common_util.any_overlaps(["Melee","Ranged","Bonus","Thrown","Light","Versatile"], this.modifies_details) || 
  //           this.weapon_keywords.length > 0 || this.excluded_weapon_keywords.length > 0) {
  //           this.apply_to.push("Weapons");
  //           this.weapon_limit.attack_types = this.modifies_details.filter(o => ["Melee","Ranged","Bonus","Thrown","Light","Versatile"].includes(o)) as AttackType[];
  //           this.weapon_limit.weapon_keyword_ids = this.weapon_keywords.filter(o => o !== "Any");
  //           this.weapon_limit.excluded_keyword_ids = this.excluded_weapon_keywords;
  //           this.weapon_limit.damage_types = this.the_damage_types.filter(o => DAMAGE_TYPES.includes(o)) as DamageType[];
  //         }
  //         if (common_util.any_overlaps(["Melee Spell","Ranged Spell","Save","Spell Attacks","Cantrip Attacks"], this.modifies_details) ||
  //           this.class_ids.length > 0 || this.slot_levels.length > 0 || 
  //           this.magic_schools.length > 0 || this.spell_list_ids.length > 0 ||
  //           this.damage_selectable_key.length > 0) {
  //           this.apply_to.push("Spells");
  //           this.spell_limit.attack_types = this.modifies_details.filter(o => ["Melee Spell","Ranged Spell","Save"].includes(o)) as AttackType[];
  //           this.spell_limit.damage_types = this.the_damage_types.filter(o => DAMAGE_TYPES.includes(o)) as DamageType[];
  //           if (this.damage_selectable_key.length > 0) {
  //             this.spell_limit.damage_types.push("Selectable");
  //             this.spell_limit.damage_selectable_key = this.damage_selectable_key;
  //           }
  //           this.spell_limit.levels = this.slot_levels.includes("Any") ? [] : this.slot_levels.map(o => {
  //             if (o === "Cantrip") {
  //               return 0;
  //             } else {
  //               return +o as SpellLevel;
  //             }
  //           });
  //           this.spell_limit.schools = this.magic_schools.filter(o => SCHOOLS.includes(o)); 
  //           this.spell_limit.list_ids = this.spell_list_ids;
  //           if (this.class_ids.length > 0) {
  //             this.spell_limit.source_type = "game_class";
  //             this.spell_limit.source_ids = this.class_ids;
  //           }
  //         }

  //         // this.modifies_details = [];
  //         // this.weapon_keywords = [];
  //         // this.excluded_weapon_keywords = [];
  //         // this.the_damage_types = [];
  //         // this.damage_selectable_key = "";
  //         // this.slot_levels = [];
  //         // this.magic_schools = [];
  //         // this.spell_list_ids = [];
  //         // this.class_ids = [];
  //       }
  //     } 
  //     if (this.allowed_armor_types.includes("ALL")) {
  //       this.allowed_armor_types = ["Any"];
  //     }
  //     if (obj.amount_unb) {
  //       this.amount_unb = new UpgradableNumber(obj.amount_unb);
  //     } else {
  //       try {
  //         this.amount_unb = new UpgradableNumber(+this.old_amount);
  //       } catch (ex) {}
  //     }
  //     this.mod_type = obj.mod_type ? obj.mod_type : "Add";
  //     // if (this.modifies === "Unarmed Strikes") {
  //     //   console.log(obj);
  //     //   console.log(this);
  //     // }
  //     for (let i = 0; i < this.modifies_details.length; ++i) {
  //       switch(this.modifies_details[i]) {
  //         case "Strength": this.modifies_details[i] = "STR"; break;
  //         case "Dexterity": this.modifies_details[i] = "DEX"; break;
  //         case "Constitution": this.modifies_details[i] = "CON"; break;
  //         case "Intelligence": this.modifies_details[i] = "INT"; break;
  //         case "Wisdom": this.modifies_details[i] = "WIS"; break;
  //         case "Charisma": this.modifies_details[i] = "CHA"; break;
  //       }
  //     }
  //   }
  // }

  get damage_types() {
    return this.the_damage_types.filter(o => DAMAGE_TYPES.includes(o)) as DamageType[];
  }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    const upgradable_number_array: any[] = [];
    this.upgradable_number_array.forEach(un => {
      upgradable_number_array.push(un.toDBObj());
    });
    return {
      ...db_obj,
      modifies: this.modifies,
      modifies_details: this.modifies_details,
      class_ids: this.class_ids,
      modifies_detail_2: this.modifies_detail_2,
      modifies_detail_3: this.modifies_detail_3,
      multiply: this.multiply,
      replace_asm: this.replace_asm,
      type: this.type,
      // amount: this.amount,
      allowed_armor_types: this.allowed_armor_types,
      required_armor_types: this.required_armor_types,
      excluded_weapon_keywords: this.excluded_weapon_keywords,
      // required_weapon_keywords: this.required_weapon_keywords,
      upgradable_number_array,
      rolls: this.rolls.toDBObj(),
      amount_unb: this.amount_unb.toDBObj(),
      mod_type: this.mod_type,
      apply_to: this.apply_to,
      spell_limit: this.spell_limit.toDBObj(),
      weapon_limit: this.weapon_limit.toDBObj(),
      ability_limit: this.ability_limit.toDBObj(),
      // weapon_keywords: this.weapon_keywords,
      // magic_schools: this.magic_schools,
      // the_damage_types: this.the_damage_types,
      // damage_selectable_key: this.damage_selectable_key,
      // slot_levels: this.slot_levels,
      // spell_list_ids: this.spell_list_ids,
      selectable_key: this.selectable_key,
      ability_scores: this.ability_scores,
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof Modifier) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: Modifier) {
    this.modifies = copyMe.modifies;
    this.modifies_details = [...copyMe.modifies_details];
    this.class_ids = [...copyMe.class_ids];
    this.modifies_detail_2 = copyMe.modifies_detail_2;
    this.modifies_detail_3 = copyMe.modifies_detail_3;
    this.type = copyMe.type;
    this.multiply = copyMe.multiply;
    this.replace_asm = copyMe.replace_asm;
    this.old_amount = copyMe.old_amount;
    this.allowed_armor_types = [...copyMe.allowed_armor_types];
    this.required_armor_types = [...copyMe.required_armor_types];
    this.excluded_weapon_keywords = [...copyMe.excluded_weapon_keywords];
    // this.required_weapon_keywords = [...copyMe.required_weapon_keywords];
    this.upgradable_number_array = [];
    copyMe.upgradable_number_array.forEach(un => {
      this.upgradable_number_array.push(un.clone());
    });
    this.rolls = copyMe.rolls.clone();
    this.amount_unb = copyMe.amount_unb.clone();
    this.mod_type = copyMe.mod_type;
    // this.weapon_keywords = [...copyMe.weapon_keywords];
    // this.magic_schools = [...copyMe.magic_schools];
    // this.the_damage_types = [...copyMe.the_damage_types];
    // this.damage_selectable_key = copyMe.damage_selectable_key;
    // this.slot_levels = [...copyMe.slot_levels];
    this.selectable_key = copyMe.selectable_key;
    // this.spell_list_ids = [...copyMe.spell_list_ids];
    this.ability_scores = [...copyMe.ability_scores];
    this.apply_to = [...copyMe.apply_to];
    this.weapon_limit = copyMe.weapon_limit.clone();
    this.ability_limit = copyMe.ability_limit.clone();
    this.spell_limit = copyMe.spell_limit.clone();
  }

  clone(drop_ids = false): Modifier {
    const new_obj = new Modifier();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}